import { TagPreferenceType } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { UseMutationResult } from "@tanstack/react-query";
import { Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PreferenceCloud } from "./PreferenceCloud";
import { useCategoryStore } from "@/stores/use-tutorial-progress";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { MixpanelActions } from "@/integrations/mixpanel/mixpanel";

interface TagCloudProps {
  includedTags?: string[];
  excludedTags?: string[];
  updateTagPreference: UseMutationResult<
    void,
    Error,
    { tagName: string; preferenceType: TagPreferenceType | null }
  >;
  isLargeScreen: boolean;
}

export const TagCloud = ({
  includedTags = [],
  excludedTags = [],
  updateTagPreference,
  isLargeScreen,
}: TagCloudProps) => {
  const subscription = useSubscription();
  const { t } = useTranslation();
  const categories = useCategoryStore((store) => store.categories);
  const curSelection = useCategoryStore((store) => store.currentSelection);
  const curIndex = categories.findIndex(
    (value) => value.documentId === curSelection?.category.documentId
  );

  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    if (curIndex !== -1) {
      setExpandedSections((prev) => {
        return {
          ...prev,
          [curIndex]: true,
        };
      });
    }
  }, [curIndex]);

  const toggleSection = (index) => {
    setExpandedSections((prev) => {
      const isCurrentlyOpen = !!prev[index];
      if (!isCurrentlyOpen) {
        MixpanelActions.openCategory(categories[index]);
      }
      return {
        ...prev,
        [index]: !isCurrentlyOpen,
      };
    });
  };

  return (
    <div
      className={cn(
        '"max-w-md mx-auto bg-transparent lg:px-6 pt-0 rounded-lg shadow-sm"',
        {
          "max-w-md": isLargeScreen,
          "max-w-full": !isLargeScreen,
        }
      )}
    >
      <div className="space-y-1">
        {categories.map((category, index) => {
          let total = 0;
          let completed = 0;

          for (const playlist of category.playlists) {
            for (const tutorial of playlist.tutorials) {
              total += 1;
              if (tutorial.is_completed) completed += 1;
            }
          }

          if (total === 0) return null;
          const completionPercent = Math.round((completed / total) * 100);
          if (completionPercent === 100) return null;

          return (
            <div key={`category-${index}`} className="group">
              <div
                className={cn(
                  "flex items-center justify-between py-3 px-2 hover:bg-gray-50 dark:hover:bg-primary rounded-lg cursor-pointer transition-colors duration-200",
                )}
                onClick={() => {
                    toggleSection(index);
                }}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    {expandedSections[index] ? (
                      <Minus className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    ) : (
                      <Plus className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    )}
                  </div>
                  <span className="text-gray-800 font-medium text-base dark:text-white">
                    {category.title}
                  </span>
                </div>

                <div className="flex items-center">
                  <span className={`text-sm font-medium`}>
                    {completionPercent}%
                  </span>
                  {completionPercent > 20 && (
                    <div className="w-1.5 h-1.5 relative bottom-1 bg-blue-500 rounded-full"></div>
                  )}
                </div>
              </div>

              {/* TODO: fix types */}
              {expandedSections[index] && (
                <PreferenceCloud
                  title={t("common.popularTags")}
                  items={category.playlists.map((playlist) => ({
                    id: playlist.documentId,
                    name: playlist.title,
                    is_trial: playlist.is_trial,
                  }))}
                  currentCategory={category.documentId}
                  isLoading={false}
                  selectedItems={includedTags}
                  excludedItems={excludedTags}
                  updatePreference={{
                    ...updateTagPreference,
                    mutate: ({ name, preferenceType }) =>
                      updateTagPreference.mutate({
                        tagName: name,
                        preferenceType,
                      }),
                  }}
                  renderItemLabel={(tag) =>
                    tag.name === "completed"
                      ? t("tags.completed")
                      : tag.name === "not interested"
                      ? t("tags.notInterested")
                      : tag.name
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
